/**
 * 서비스/카테고리/기간/매장 필터 바 — Mogoo 대시보드와 ModernWash 운영 화면이 공유한다.
 *
 * 경계 원칙:
 *   - service 는 두 갈래뿐: 'mogoo'(네이티브만) | 'modernwash'(레거시 미러만). "통합 전체" 없음.
 *   - 카테고리는 백엔드 taxonomy 로만 만든다(하드코딩 금지). service 바뀌면 카테고리는 '전체'로 리셋.
 *   - 매장 선택은 master 만. manager 는 서버가 본인 매장으로 고정 → 셀렉터 숨기고 매장명만 표시.
 *
 * 상태는 부모가 URL(useSearchParams)로 들고, 이 컴포넌트는 값을 받아 그리고 patch 를 올린다(controlled).
 * 접근성/테스트를 위해 select 는 native <select>(role=combobox)로, 월/범위는 native date input 으로 둔다.
 */
import React from 'react';
import { Box, Typography, ToggleButton, ToggleButtonGroup, Chip } from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { segmentedControlSx } from '../../styles/uiTokens';
import {
  SERVICE_OPTIONS,
  PERIOD_OPTIONS,
  categoriesForService,
  currentYm,
} from './mirrorFormat';

const fieldSx = {
  height: 36,
  borderRadius: '8px',
  border: '1px solid rgba(0,0,0,0.16)',
  backgroundColor: '#fff',
  px: 1.25,
  fontSize: 13,
  fontWeight: 600,
  color: 'text.primary',
  fontFamily: 'inherit',
  cursor: 'pointer',
  outline: 'none',
  '&:focus': { borderColor: 'primary.main' },
};

const FieldLabel = ({ children }) => (
  <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', mb: 0.5, letterSpacing: '-0.01em' }}>
    {children}
  </Typography>
);

const MirrorFilterBar = ({
  value,
  onChange,
  taxonomy,
  stores = [],
  role,
  managerStoreName,
  showService = true,
  rightSlot = null,
}) => {
  const { service, category = '', period, month = '', start = '', end = '', storeId = '' } = value;
  const categories = categoriesForService(taxonomy, service);
  const isMaster = role === 'master';

  const handleService = (_e, next) => {
    if (!next || next === service) return;
    // 서비스가 바뀌면 카테고리 축이 달라지므로 '전체'로 초기화한다(잘못된 카테고리 전송 방지).
    onChange({ service: next, category: '' });
  };

  const handlePeriod = (_e, next) => {
    if (!next || next === period) return;
    const patch = { period: next };
    if (next === 'month' && !month) patch.month = currentYm();
    onChange(patch);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        gap: { xs: 1.5, md: 2 },
        p: { xs: 1.5, md: 2 },
        mb: 3,
        borderRadius: '12px',
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: '#fff',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
      }}
    >
      {/* 서비스 스코프 */}
      {showService && (
        <Box>
          <FieldLabel>서비스</FieldLabel>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={service}
            onChange={handleService}
            aria-label="서비스 스코프"
            sx={segmentedControlSx}
          >
            {SERVICE_OPTIONS.map((o) => (
              <ToggleButton key={o.key} value={o.key} aria-label={o.label}>
                {o.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}

      {/* 카테고리 (백엔드 taxonomy 기반) */}
      <Box>
        <FieldLabel>카테고리</FieldLabel>
        <Box
          component="select"
          aria-label="카테고리"
          value={category}
          onChange={(e) => onChange({ category: e.target.value })}
          sx={{ ...fieldSx, minWidth: 130 }}
        >
          <option value="">{service === 'mogoo' ? '전체 카테고리' : '전체'}</option>
          {categories.map((c) => (
            <option key={c.key} value={c.key}>
              {c.label}
            </option>
          ))}
        </Box>
      </Box>

      {/* 기간 */}
      <Box>
        <FieldLabel>기간</FieldLabel>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={period}
          onChange={handlePeriod}
          aria-label="기간"
          sx={segmentedControlSx}
        >
          {PERIOD_OPTIONS.map((o) => (
            <ToggleButton key={o.key} value={o.key} aria-label={o.label}>
              {o.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* 월 선택 */}
      {period === 'month' && (
        <Box>
          <FieldLabel>월</FieldLabel>
          <Box
            component="input"
            type="month"
            aria-label="월 선택"
            value={month || currentYm()}
            onChange={(e) => onChange({ month: e.target.value })}
            sx={{ ...fieldSx }}
          />
        </Box>
      )}

      {/* 사용자 지정 범위 */}
      {period === 'range' && (
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
          <Box>
            <FieldLabel>시작일</FieldLabel>
            <Box
              component="input"
              type="date"
              aria-label="시작일"
              value={start}
              max={end || undefined}
              onChange={(e) => onChange({ start: e.target.value })}
              sx={{ ...fieldSx }}
            />
          </Box>
          <Typography sx={{ pb: 1, color: 'text.disabled' }}>~</Typography>
          <Box>
            <FieldLabel>종료일</FieldLabel>
            <Box
              component="input"
              type="date"
              aria-label="종료일"
              value={end}
              min={start || undefined}
              onChange={(e) => onChange({ end: e.target.value })}
              sx={{ ...fieldSx }}
            />
          </Box>
        </Box>
      )}

      {/* 매장 — master 만 선택, manager 는 서버 고정(셀렉터 숨김) */}
      <Box>
        <FieldLabel>매장</FieldLabel>
        {isMaster ? (
          <Box
            component="select"
            aria-label="매장"
            value={storeId}
            onChange={(e) => onChange({ storeId: e.target.value })}
            sx={{ ...fieldSx, minWidth: 150 }}
          >
            <option value="">ModernWash 전체</option>
            {stores.map((s) => (
              <option key={s._id} value={s._id}>
                {s.name}
              </option>
            ))}
          </Box>
        ) : (
          <Chip
            icon={<StorefrontIcon sx={{ fontSize: 16 }} />}
            label={managerStoreName || '내 매장'}
            variant="outlined"
            sx={{ height: 36, borderRadius: '8px', fontWeight: 700 }}
          />
        )}
      </Box>

      {rightSlot ? <Box sx={{ ml: { md: 'auto' }, alignSelf: 'flex-end' }}>{rightSlot}</Box> : null}
    </Box>
  );
};

export default MirrorFilterBar;
